import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import qs from 'qs';

import PageContentWrapper from '../components/pageContentWrapper';
import { PageContext } from '../components/page';
import { BodyCard } from '../components/styledComponent';
import { ManageTripGroupFilters } from './ManageTripGroupsFilters';
import { SortInfo, DirectionType } from '../../components/table/Table';
import { OrangeLinkHeader } from '../../components/OrangeLink';
import { isOverriddenDisplayName } from './ManageTripGroupsFilters';
import {
  searchTripGroups,
  tgSearchParamObjectToQS,
  getTripGroupAttributes,
  SearchTripGroupsArgs,
} from '../../service/tripGroupService';
import { SearchResultsList, preSearchDummyId } from './SearchResultsList';
import { TripGroupView } from '../../types/tripGroup';
import { CreateTripGroupPath } from '../../appRoutes';
import { getMessageConfigurations } from '../../service/messageConfigurationService';
import { MessageConfiguration } from '../../types/messageConfiguration';
import { CONDUCIVE_USER_ACCOUNT_ID } from '../../constants';
import { getProperty } from '../../fsConfig';

const defaultSort = 'lineage';

const headers = [
  {
    header: 'Lineage',
    headerValue: defaultSort,
    canSort: true,
  },
  {
    header: 'Name',
    headerValue: 'name',
    canSort: true,
  },
  {
    header: 'Created (UTC)',
    headerValue: 'createdAt',
    canSort: true,
  },
  { header: isOverriddenDisplayName },
  { header: 'Trip Assist Issues' },
  { header: 'Agent Trip Alerts' },
  { header: 'Traveler Trip Alerts' },
];

const ManageTripGroupsContainer: React.FC = () => {
  const { pushAlert } = useContext(PageContext);
  const user = getProperty('session', {});
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [searchParams, setSearchParams] = useState<SearchTripGroupsArgs>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  // start with dummy state so the result set knows to not show "no results" message and hide this dummy result
  const [searchResults, setSearchResults] = useState<TripGroupView[]>([
    {
      id: preSearchDummyId,
      lineage: '',
      createAtUtc: '2022-12-07T05:21:53.623Z',
      tripGroupViewSettings: {
        name: '',
        hidden: false,
      },
    },
  ]);
  const [hasSearchError, setHasSearchError] = useState(false);
  const [accountAttributesHierarchy, setAccountAttributesHierarchy] = useState<
    any
  >({
    accountAttributes: [],
    root: {
      attribute: '',
      isOverridden: false,
      value: '',
      children: [],
    },
  });
  const [sortInfo, setSortInfo] = useState<SortInfo>({
    sortedFieldValue: '',
    direction: 'asc',
  });
  const [messageConfigs, setMessageConfigs] = useState<MessageConfiguration[]>(
    [],
  );

  const onSort = (header: string) => {
    const direction =
      sortInfo.sortedFieldValue === header && sortInfo.direction === 'asc'
        ? 'desc'
        : 'asc';
    setSortInfoFields(direction, header);
    const convertedSort = `${direction === 'asc' ? '' : '-'}${header}`;
    searchParams.sort = convertedSort;
    onSubmit(searchParams, true);
  };

  const setSortInfoFields = (direction: DirectionType, field: string) => {
    const sort = { ...sortInfo };
    sort.direction = direction;
    sort.sortedFieldValue = field;
    setSortInfo(sort);
  };

  const getConfigs = async () => {
    try {
      const response = await getMessageConfigurations();
      const options = [
        {
          id: 'Any',
          name: 'Any',
          accountId: 1,
          archived: false,
          messageConfigurationValues: {},
        },
        ...response,
      ];
      setMessageConfigs(options);
    } catch (error) {
      setMessageConfigs([]);
      if (error instanceof Error) {
        pushAlert('danger', `Failed to load message configuration. ${error.message}`);
      } else {
        pushAlert('danger', 'Failed to load message configuration. Please contact support or try again later.');
      }
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (user?.accountId === CONDUCIVE_USER_ACCOUNT_ID) {
        pushAlert('conducive');
      } else {
        setLoading(true);
        try {
          await getConfigs();
          const result = await getTripGroupAttributes();
          setAccountAttributesHierarchy(result);
        } catch (_err) {
          if (_err instanceof Error) {
            pushAlert('danger', `Failed getting attributs. ${_err.message}`);
          } else {
            pushAlert('danger', 'Something went wrong getting attribute values to put into the lineage hierarchy picker.');
          }
        } finally {
          setLoading(false);
        }
      }
    };

    fetchData();
  }, []);

  const onSubmit = async (
    searchParams: SearchTripGroupsArgs,
    isSubmitting = true,
  ) => {
    setLoading(true);
    setIsSubmitting(true);

    if (!searchParams.sort) {
      setSortInfoFields('asc', defaultSort);
    } else {
      const sortDirection = searchParams.sort;
      const removeDescendingCharacter = sortDirection.startsWith('-')
        ? sortDirection.slice(1)
        : sortDirection;
      if (
        !headers.some(
          c => c.canSort && c.headerValue === removeDescendingCharacter,
        )
      ) {
        delete searchParams.sort;
        setSortInfoFields('asc', defaultSort);
      } else {
        setSortInfoFields(
          sortDirection?.startsWith('-') ? 'desc' : 'asc',
          sortDirection ? removeDescendingCharacter : defaultSort,
        );
      }
    }

    try {
      const response = await searchTripGroups(searchParams);
      setHasSearchError(false);
      setSearchResults(response.tripGroupViewList);
      response.tripGroupViewList.length < response.count &&
        pushAlert(
          'success',
          `Trip Group count limited to ${response.tripGroupViewList.length}`,
        );
    } catch (_err) {
      setHasSearchError(true);
      setSearchResults([]);
      if (_err instanceof Error) {
        pushAlert('danger', _err.message);
      } else {
        pushAlert('danger', 'Something went wrong getting searching for trip groups.');
      }
      pushAlert(
        'danger',
        'Something went wrong getting searching for trip groups.',
      );
    } finally {
      if (isSubmitting) {
        navigate({
          pathname: location.pathname,
          search: qs.stringify(tgSearchParamObjectToQS(searchParams)), // qs here to avoid state race conditions with setting/accessing urlsearchparams
        });
      }
      setLoading(false);
      setIsSubmitting(false);
    }
  };

  return (
    <PageContentWrapper title="MANAGE TRIP GROUPS" loading={loading}>
      <OrangeLinkHeader
        path={CreateTripGroupPath}
        text="Create New Trip Group"
      />
      <BodyCard>
        {(accountAttributesHierarchy?.accountAttributes?.length && (
          <ManageTripGroupFilters
            accountAttributeHierarchy={accountAttributesHierarchy}
            onSubmit={onSubmit}
            isSubmitting={isSubmitting}
            searchParams={searchParams}
            setSearchParams={setSearchParams}
            messageConfigs={messageConfigs}
          />
        )) ||
          (!loading &&
            'Error loading account attributes filters. If this is unexpected please contact support')}
      </BodyCard>
      <div data-fs-exclude>
      <SearchResultsList
        headers={headers}
        tripGroupData={searchResults}
        loading={loading}
        hasError={hasSearchError}
        includeHidden={!!searchParams.includeHidden}
        sortInfo={sortInfo}
        onSort={onSort}
      />
      </div>
    </PageContentWrapper>
  );
};

export default ManageTripGroupsContainer;
